import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { realtimeClient, primeRealtimeAuth } from '@/lib/supabase/realtime';
import { queryKeys } from '@/lib/query/queryClient';
import { TASK_STATUSES, type BoardColumnPage, type TaskStatus } from '@/types/api';

export type RealtimeStatus = 'connecting' | 'live' | 'reconnecting' | 'offline';

/**
 * Keeps the board in step with Postgres.
 *
 * Every change reaches here the same way regardless of what caused it — the agent
 * assigning work, someone replying in Telegram, an admin override, a cron sweep. None
 * of those are special-cased anywhere, because they all end up as one row change that
 * Postgres streams out.
 *
 * Refetches are COALESCED and TARGETED. A batch job touching thirty tasks arrives as
 * thirty events, and the columns are five independent queries — so the naive handler
 * did thirty bursts × five columns for work that touched one of them. Each burst now
 * collects the set of statuses it actually affected and refetches only those.
 *
 * The payload is treated as a signal, not data. Task rows here are raw Postgres —
 * snake_case, no joined assignee — so rendering from them would mean a second,
 * drifting mapper and a way to sidestep the response schema that acts as our field
 * allowlist. Refetching keeps one shape and one authorization path.
 */
export function useTasksRealtime(scope: string): RealtimeStatus {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<RealtimeStatus>('connecting');
  const pending = useRef<number | null>(null);
  const hadError = useRef(false);

  useEffect(() => {
    let disposed = false;

    /** Columns this burst touched. Empty means "we could not tell" — refetch all. */
    let dirtyColumns = new Set<TaskStatus>();
    let dirtyUnknown = false;
    let dirtyEventTasks = new Set<string>();

    const schedule = () => {
      if (pending.current !== null) return;
      pending.current = window.setTimeout(() => {
        pending.current = null;

        const columns = dirtyColumns;
        const unknown = dirtyUnknown;
        const eventTasks = dirtyEventTasks;
        dirtyColumns = new Set();
        dirtyUnknown = false;
        dirtyEventTasks = new Set();

        if (disposed) return;

        /*
          Invalidating an infinite query refetches EVERY page it has loaded, not just
          the first. That is what keeps a card the desks changed on page 3 correct
          without us tracking which pages are open — and why the cursor has to be a
          position in the sort rather than an offset, so replaying it lands in the same
          place after rows are inserted above it.
        */
        const targets = unknown ? TASK_STATUSES : [...columns];
        for (const column of targets) {
          void queryClient.invalidateQueries({ queryKey: ['boardColumn', scope, column] });
        }

        // Only the open card's log, and only when a row was actually appended to it.
        for (const taskId of eventTasks) {
          void queryClient.invalidateQueries({ queryKey: queryKeys.taskEvents(taskId) });
        }

        // The bell reads the same task_events rows, so anything appended anywhere may
        // change its badge. One query, one request — it does not need to know which.
        if (eventTasks.size > 0) {
          void queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
      }, 150);
    };

    /** Where the board currently believes a card lives. */
    const cachedStatusOf = (taskId: string): TaskStatus | null => {
      for (const [, page] of queryClient.getQueriesData<{ pages: BoardColumnPage[] }>({
        queryKey: ['boardColumn'],
      })) {
        for (const p of page?.pages ?? []) {
          if (p.items.some((task) => task.id === taskId)) return p.status;
        }
      }
      return null;
    };

    const cachedVersionOf = (taskId: string): number | null => {
      for (const [, page] of queryClient.getQueriesData<{ pages: BoardColumnPage[] }>({
        queryKey: ['boardColumn'],
      })) {
        const found = page?.pages.flatMap((p) => p.items).find((task) => task.id === taskId);
        if (found) return found.version;
      }
      return null;
    };

    const markColumn = (value: unknown) => {
      if (typeof value === 'string' && (TASK_STATUSES as readonly string[]).includes(value)) {
        dirtyColumns.add(value as TaskStatus);
        return true;
      }
      return false;
    };

    // A unique name per mount, so React 19 StrictMode's double-invoke cannot collide
    // with an existing channel — Supabase throws on re-subscribing the same one.
    const channel = realtimeClient.channel(`board:${scope}:${Math.random().toString(36).slice(2)}`);

    channel
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, (payload) => {
        const row = (payload.new ?? {}) as Record<string, unknown>;
        const id = typeof row['id'] === 'string' ? row['id'] : null;
        if (!id) return;

        /**
         * Opening a card writes to it — assignee_read_at — so our own read comes
         * straight back as an event. `version` is the whole test: every real change
         * bumps it, and app.mark_task_read deliberately does not, precisely so reading
         * cannot invalidate a desk's ifRevision check. Compared on version alone and
         * not updated_at, because Realtime hands us raw Postgres text while the API
         * hands us an ISO string, and those never compare equal.
         */
        const known = cachedVersionOf(id);
        if (known !== null && typeof row['version'] === 'number' && known >= row['version']) return;

        markColumn(row['status']);

        /**
         * A card that MOVED leaves a column as well as joining one, and the old status
         * is not in the payload — REPLICA IDENTITY is DEFAULT, so `old` carries only
         * the primary key. The cache knows where we last saw it, which covers every
         * card on screen. For one we have never loaded, the status_changed audit row
         * arriving in this same burst carries from_status, and that covers the rest.
         */
        const previous = cachedStatusOf(id);
        if (previous) dirtyColumns.add(previous);

        schedule();
      })
      // task_events carries the notes the desks append, which drive the unread dot.
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'task_events' }, (payload) => {
        const row = (payload.new ?? {}) as Record<string, unknown>;
        const taskId = typeof row['task_id'] === 'string' ? row['task_id'] : null;
        if (taskId) dirtyEventTasks.add(taskId);

        // `created` rows carry to_status; `status_changed` carries both. Between them
        // every move is fully described without a second lookup.
        const named = [markColumn(row['to_status']), markColumn(row['from_status'])].some(Boolean);

        if (!named) {
          // A note or a field change. It shifts the unread count of whichever column
          // holds the card — and if we have never loaded that card, nothing here says
          // which one that is, so this is the one case that has to ask all five.
          const known = taskId ? cachedStatusOf(taskId) : null;
          if (known) dirtyColumns.add(known);
          else dirtyUnknown = true;
        }

        schedule();
      })
      .subscribe((state: string) => {
        if (disposed) return;

        if (state === 'SUBSCRIBED') {
          setStatus('live');
          // Realtime has no replay, so anything that changed while the socket was down
          // was simply never delivered. This is the one correct place to refetch.
          if (hadError.current) {
            hadError.current = false;
            void queryClient.invalidateQueries({ queryKey: ['boardColumn', scope] });
          }
          return;
        }

        if (state === 'CHANNEL_ERROR' || state === 'TIMED_OUT') {
          hadError.current = true;
          setStatus('reconnecting');
          return;
        }

        if (state === 'CLOSED') setStatus('offline');
      });

    void primeRealtimeAuth();

    return () => {
      disposed = true;
      if (pending.current !== null) window.clearTimeout(pending.current);
      void realtimeClient.removeChannel(channel);
    };
  }, [scope, queryClient]);

  return status;
}
